import { useQuery } from "@apollo/client";
import { GridColDef } from "@mui/x-data-grid";
import { ClientAllocationRow } from "_graphql-types";
import { useMemo } from "react";
import { CLIENT_ALLOCATIONS } from "./graphql";
import { emptyFields, formatMonth, totals } from "./logic";
import {
  ClientPicker,
  columnGroups,
  EmptyState,
  FilterBar,
  FundPicker,
  moneyCol,
  Page,
  percentCol,
  QueryState,
  ReportGrid,
  Section,
  ShareClassPicker,
  textCol,
  TOTAL_ROW_ID,
  useSelectedClient,
  useSelectedFund,
  useSelectedShareClass,
  useShareClasses,
} from "./shared";

type Row = Partial<ClientAllocationRow> & { id: number | string };

/** Flows and fees add up over months; balances don't (the legacy page summed them, a bug). */
const SUMMED = ["addition", "redemption", "switchIn", "switchOut", "mgmtFee", "adminFee", "incentiveFee"] as const;
const SWITCHES = ["switchIn", "switchOut"] as const;

const COLUMNS: GridColDef<Row>[] = [
  { ...textCol<Row>("date", "Month", 110), valueGetter: (_v, row) => (row.date ? formatMonth(row.date) : "Total") },
  moneyCol<Row>("begBal", "Beg. Bal"),
  moneyCol<Row>("addition", "Addition"),
  moneyCol<Row>("redemption", "Redemption"),
  moneyCol<Row>("switchIn", "Switch In"),
  moneyCol<Row>("switchOut", "Switch Out"),
  moneyCol<Row>("adjBegBal", "Adj. Beg. Bal"),
  moneyCol<Row>("mgmtFee", "Mgmt Fee"),
  moneyCol<Row>("adminFee", "Admin Fee"),
  moneyCol<Row>("incentiveFee", "Perf Fee"),
  percentCol<Row>("estGrossReturn", "Est. Gross %"),
  percentCol<Row>("estNetReturn", "Est. Net %"),
  percentCol<Row>("actGrossReturn", "Act. Gross %"),
  percentCol<Row>("actNetReturn", "Act. Net %"),
  percentCol<Row>("effGrossReturn", "Eff. Gross %"),
  percentCol<Row>("effNetReturn", "Eff. Net %"),
  moneyCol<Row>("estNetEndBal", "Est. Net End Bal", 140),
  moneyCol<Row>("actNetEndBal", "Act. Net End Bal", 140),
];

const GROUPS = [
  { groupId: "Balances", fields: ["begBal", "addition", "redemption", "switchIn", "switchOut", "adjBegBal"] },
  { groupId: "Fees", fields: ["mgmtFee", "adminFee", "incentiveFee"] },
  { groupId: "Returns", fields: ["estGrossReturn", "estNetReturn", "actGrossReturn", "actNetReturn", "effGrossReturn", "effNetReturn"] },
  { groupId: "End Balance", fields: ["estNetEndBal", "actNetEndBal"] },
];

export function OverTimeReport() {
  const [fundId, setFundId] = useSelectedFund();
  const [clientId, setClientId] = useSelectedClient();
  const [shareClassId, setShareClassId] = useSelectedShareClass();
  const { shareClasses, ...scState } = useShareClasses(fundId, clientId);
  const { data, loading, error } = useQuery(CLIENT_ALLOCATIONS, {
    variables: { shareClassId },
    skip: shareClassId === null,
  });

  const rows = useMemo((): Row[] => {
    const allocations = data?.clientAllocations ?? [];
    if (!allocations.length) return [];
    // The API orders a share class's months newest first.
    const body: Row[] = allocations.map(a => ({ ...a, id: a.date }));
    return [...body, { id: TOTAL_ROW_ID, ...totals(allocations, SUMMED) }];
  }, [data]);
  const { columns, groups } = useMemo(() => {
    const empty: ReadonlySet<string> = rows.length ? emptyFields(rows, SWITCHES) : new Set();
    const shown = COLUMNS.filter(c => !empty.has(c.field));
    return { columns: shown, groups: columnGroups(shown, GROUPS) };
  }, [rows]);
  const selected = shareClasses.find(s => s.shareClassId === shareClassId);

  return (
    <Page title="Over Time Report" description="One share class, month by month. Flows and fees are totalled; balances and returns are not.">
      <FilterBar>
        <FundPicker
          value={fundId}
          onChange={id => {
            setFundId(id);
            setShareClassId(null);
          }}
        />
        <ClientPicker
          rcgFundId={fundId}
          value={clientId}
          onChange={id => {
            setClientId(id);
            setShareClassId(null);
          }}
        />
        <ShareClassPicker shareClasses={shareClasses} value={shareClassId} onChange={setShareClassId} />
      </FilterBar>
      <QueryState loading={loading || scState.loading} error={error ?? scState.error} />
      {shareClassId === null ? (
        <EmptyState>Pick a fund, a client and one of their share classes to see it month by month.</EmptyState>
      ) : (
        <Section title={selected ? `${selected.name}${selected.derived ? " (derived)" : ""}` : "Share class"}>
          <ReportGrid<Row> rows={rows} columns={columns} columnGroupingModel={groups} noRowsLabel="No months for this share class" />
        </Section>
      )}
    </Page>
  );
}
